import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col, Table } from 'react-bootstrap';
import { invoiceService, clientService, supplierService, productService } from '../../services/api';
import { Client, Supplier, Product } from '../../types';
import { toast } from 'react-toastify';
import { PlusIcon, TrashIcon } from '../common/Icons';

interface InvoiceFormProps {
  show: boolean;
  onHide: () => void;
  onSuccess: () => void;
  invoice?: any;
}

interface InvoiceItem {
  product_id: number | null;
  product_name: string;
  description: string;
  quantity: number;
  unit_price: number;
  is_free_service: boolean;
}

const InvoiceForm: React.FC<InvoiceFormProps> = ({ show, onHide, onSuccess, invoice }) => {
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [formData, setFormData] = useState({
    type: 'sale',
    client_id: '',
    supplier_id: '',
    date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    notes: '',
    terms: '',
  });
  const [items, setItems] = useState<InvoiceItem[]>([]);

  useEffect(() => {
    if (show) {
      fetchClients();
      fetchSuppliers();
      fetchProducts();
      
      if (invoice) {
        // Mode édition
        setFormData({
          type: invoice.type,
          client_id: invoice.client_id?.toString() || '',
          supplier_id: invoice.supplier_id?.toString() || '',
          date: invoice.date,
          due_date: invoice.due_date,
          notes: invoice.notes || '',
          terms: invoice.terms || '',
        });
        
        setItems(invoice.items?.map((item: any) => ({
          product_id: item.product_id,
          product_name: item.product_name,
          description: item.description || '',
          quantity: item.quantity,
          unit_price: item.unit_price,
          is_free_service: !item.product_id,
        })) || []);
      } else {
        resetForm();
      }
    }
  }, [show, invoice]);

  const fetchClients = async () => {
    try {
      const response = await clientService.getAll();
      setClients(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des clients');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await supplierService.getAll();
      setSuppliers(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des fournisseurs');
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await productService.getAll();
      setProducts(response.data.data);
    } catch (error) {
      console.error('Erreur lors du chargement des produits');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addItem = () => {
    setItems(prev => [...prev, {
      product_id: null,
      product_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      is_free_service: false,
    }]);
  };

  const addFreeService = () => {
    setItems(prev => [...prev, {
      product_id: null,
      product_name: '',
      description: '',
      quantity: 1,
      unit_price: 0,
      is_free_service: true,
    }]);
  };

  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const updateItem = (index: number, field: keyof InvoiceItem, value: any) => {
    setItems(prev => prev.map((item, i) => {
      if (i === index) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'product_id' && value && !item.is_free_service) {
          const product = products.find(p => p.id === parseInt(value));
          if (product) {
            updatedItem.product_name = product.name;
            updatedItem.description = product.description || '';
            updatedItem.unit_price = formData.type === 'sale' ? product.selling_price_ht : product.purchase_price;
          }
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error('Veuillez ajouter au moins un produit');
      return;
    }

    setLoading(true);
    try {
      const data = {
        ...formData,
        client_id: formData.type === 'sale' ? parseInt(formData.client_id) : undefined,
        supplier_id: formData.type === 'purchase' ? parseInt(formData.supplier_id) : undefined,
        items: items.map(item => ({
          product_id: item.is_free_service ? null : item.product_id,
          product_name: item.product_name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
        })),
      };

      if (invoice) {
        await invoiceService.update(invoice.id, data);
        toast.success('Facture modifiée avec succès');
      } else {
        await invoiceService.create(data);
        toast.success('Facture créée avec succès');
      }
      onSuccess();
      onHide();
      resetForm();
    } catch (error) {
      toast.error(invoice ? 'Erreur lors de la modification de la facture' : 'Erreur lors de la création de la facture');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'sale',
      client_id: '',
      supplier_id: '',
      date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      notes: '',
      terms: '',
    });
    setItems([]);
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{invoice ? 'Modifier la facture' : 'Nouvelle facture'}</Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body>
          <Row>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Type *</Form.Label>
                <Form.Select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                >
                  <option value="sale">Vente</option>
                  <option value="purchase">Achat</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>
                  {formData.type === 'sale' ? 'Client *' : 'Fournisseur *'}
                </Form.Label>
                {formData.type === 'sale' ? (
                  <Form.Select
                    name="client_id"
                    value={formData.client_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Sélectionner un client</option>
                    {clients.map(client => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Select
                    name="supplier_id"
                    value={formData.supplier_id}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Sélectionner un fournisseur</option>
                    {suppliers.map(supplier => (
                      <option key={supplier.id} value={supplier.id}>
                        {supplier.name}
                      </option>
                    ))}
                  </Form.Select>
                )}
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Date *</Form.Label>
                <Form.Control
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className="mb-3">
                <Form.Label>Échéance *</Form.Label>
                <Form.Control
                  type="date"
                  name="due_date"
                  value={formData.due_date}
                  onChange={handleInputChange}
                  required
                />
              </Form.Group>
            </Col>
          </Row>

          <div className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h6>Produits et Services</h6>
              <div>
                <Button variant="outline-primary" size="sm" onClick={addItem} className="me-2">
                  <PlusIcon className="me-1" />
                  Produit
                </Button>
                <Button variant="outline-success" size="sm" onClick={addFreeService}>
                  <PlusIcon className="me-1" />
                  Service libre
                </Button>
              </div>
            </div>
            
            {items.length > 0 && (
              <Table bordered>
                <thead>
                  <tr>
                    <th>Produit/Service</th>
                    <th>Description</th>
                    <th>Quantité</th>
                    <th>Prix unitaire</th>
                    <th>Total</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        {item.is_free_service ? (
                          <Form.Control
                            type="text"
                            placeholder="Nom du service"
                            value={item.product_name}
                            onChange={(e) => updateItem(index, 'product_name', e.target.value)}
                            required
                          />
                        ) : (
                          <Form.Select
                            value={item.product_id || ''}
                            onChange={(e) => updateItem(index, 'product_id', parseInt(e.target.value))}
                            required
                          >
                            <option value="">Sélectionner un produit</option>
                            {products.map(product => (
                              <option key={product.id} value={product.id}>
                                {product.name}
                              </option>
                            ))}
                          </Form.Select>
                        )}
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          placeholder="Description"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value))}
                          required
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          value={item.unit_price}
                          onChange={(e) => updateItem(index, 'unit_price', parseFloat(e.target.value))}
                          required
                        />
                      </td>
                      <td>{(item.quantity * item.unit_price).toFixed(2)} MAD</td>
                      <td>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => removeItem(index)}
                        >
                          <TrashIcon />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Notes</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Conditions</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="terms"
                  value={formData.terms}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (invoice ? 'Modification...' : 'Création...') : (invoice ? 'Modifier la facture' : 'Créer la facture')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default InvoiceForm;